import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';

import { dashboardV2QueryKeys } from '../api/queryKeys';
import { MissingTimesheetShift, MissingTimesheetWorker } from '../api/types';
import { formatShiftDateTime } from '../util/dateFormat';

import useAuth from '@/hooks/useAuth';
import { axiosRubyClient } from '@/util/axios/axiosClient';

// Resolve one missing timesheet from the Missing Timesheet queue (GRAVY-10307).
//
// The queue could identify a payroll exception but offered no way to act on it:
// Shift Detail's worker rows are informational, and "View job" is a generic
// job-level route that drops the worker and occurrence. Ops had to leave the
// workflow and reconstruct the record — slow, and an easy way to enter hours
// against the wrong worker or shift.
//
// ## Why this posts to the existing endpoint
//
// `/api/admin/timesheets/resolve_enter_hours` is the authoritative Ops
// time-entry policy (Timesheets::ResolveEnterHoursService): it creates the
// timesheet when none exists, validates work time against the break, logs the
// ops check-in/out shift events, approves, and re-runs job status. Writing a
// second time-entry path for this queue would mean two policies that drift.
// This dialog only collects the times and supplies the exact context.
//
// It is deliberately NOT the Job page's EnterHoursModal: that component is
// bound to the Job page's timesheet/job shapes and finishes with
// `window.location.reload()`, which would tear down the queue, its scroll
// position and its filters — the opposite of what this ticket asks for. Here
// success invalidates the queue and the summary badge instead, so both refresh
// in place.

type Props = {
  shift: MissingTimesheetShift;
  worker: MissingTimesheetWorker;
  onClose: () => void;
};

const BREAK_OPTIONS = [0, 15, 30, 45, 60];

const EnterHoursDialog = ({ shift, worker, onClose }: Props) => {
  const { currentAdmin, isCurrentCustomerAdmin } = useAuth();
  // Guarded on null: `isCurrentCustomerAdmin` reads `.customer` off its
  // argument and throws while the session is still resolving.
  const clientAdmin = !!currentAdmin && isCurrentCustomerAdmin(currentAdmin);

  const queryClient = useQueryClient();
  const [checkIn, setCheckIn] = useState('');
  const [checkOut, setCheckOut] = useState('');
  const [breakMinutes, setBreakMinutes] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  // Focus is returned to whatever opened the dialog, so keyboard users land
  // back on the row they were working rather than at the top of the queue.
  const openerRef = useRef<Element | null>(null);

  useEffect(() => {
    openerRef.current = document.activeElement;
    dialogRef.current?.querySelector('input')?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      (openerRef.current as HTMLElement | null)?.focus?.();
    };
  }, [onClose]);

  const submit = async (saveOnly: boolean) => {
    if (!checkIn || !checkOut) {
      setError('Enter both a check-in and a check-out time.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await axiosRubyClient.post('/api/admin/timesheets/resolve_enter_hours', {
        jobId: shift.job_id,
        workerId: worker.worker_id,
        // The exact occurrence. Without it the server falls back to the job's
        // first shift, which on a multi-shift job is the wrong one.
        shiftId: shift.shift_id,
        checkinTime: checkIn,
        checkoutTime: checkOut,
        breakMinutes,
        timezone: shift.timezone,
        saveOnly,
      });
      // The resolved row leaves the queue and the badge drops, without the
      // full page reload the Job page's modal does.
      await queryClient.invalidateQueries({
        queryKey: dashboardV2QueryKeys.root,
      });
      onClose();
    } catch (e) {
      const message =
        // The service reports validation failures (e.g. break longer than the
        // shift) in `errors`; surface them rather than a generic failure, so
        // the admin can correct and retry.
        (e as { response?: { data?: { errors?: string[] } } })?.response?.data
          ?.errors?.[0] ?? 'Could not save these hours. Please try again.';
      setError(message);
      setSubmitting(false);
    }
  };

  return (
    <div className="dashv2-ehoverlay" role="presentation">
      <div
        className="dashv2-ehdialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="dashv2-eh-title"
        ref={dialogRef}
      >
        <h2 className="dashv2-ehtitle" id="dashv2-eh-title">
          Enter hours
        </h2>

        {/* The record being resolved, spelled out: a queue row is only safe to
            act on if the worker, occurrence and job are unambiguous. */}
        <dl className="dashv2-ehcontext">
          <div>
            <dt>Worker</dt>
            <dd>{worker.name || 'Unknown worker'}</dd>
          </div>
          <div>
            <dt>Shift</dt>
            <dd>
              {formatShiftDateTime(
                shift.start_at,
                shift.end_at,
                shift.timezone,
              )}
            </dd>
          </div>
          <div>
            <dt>Job</dt>
            <dd>
              {shift.client_name || '—'}
              {shift.skill_name ? ` · ${shift.skill_name}` : ''} (#
              {shift.job_id})
            </dd>
          </div>
          <div>
            <dt>Captured time</dt>
            <dd>None</dd>
          </div>
        </dl>

        <div className="dashv2-ehfields">
          <label className="dashv2-ehfield">
            <span>Check-in</span>
            <input
              type="time"
              value={checkIn}
              onChange={(e) => setCheckIn(e.target.value)}
            />
          </label>
          <label className="dashv2-ehfield">
            <span>Check-out</span>
            <input
              type="time"
              value={checkOut}
              onChange={(e) => setCheckOut(e.target.value)}
            />
          </label>
          <label className="dashv2-ehfield">
            <span>Break</span>
            <select
              value={breakMinutes}
              onChange={(e) => setBreakMinutes(Number(e.target.value))}
            >
              {BREAK_OPTIONS.map((m) => (
                <option key={m} value={m}>
                  {m} min
                </option>
              ))}
            </select>
          </label>
        </div>

        {error && (
          <p className="dashv2-eherror" role="alert">
            {error}
          </p>
        )}

        <div className="dashv2-ehfoot">
          {/* Role-specific outcomes, taken from the authoritative Job Detail
              Resolve Timesheet flow rather than invented here (GRAVY-10330):

                Tenant Admin  Save Changes (record time, do NOT approve)
                              Approve      (record time AND approve)
                Client Admin  Resolve      (record time AND approve, as the client)

              Both map onto the one service — `saveOnly` is the difference — so
              validation, calculation, audit and authorization stay identical to
              the Job page. A Client Admin never gets the save-without-approving
              choice: leaving a record pending for someone else to approve is a
              GravyWork-internal step, and the client is not that someone.

              Cancel is kept for BOTH roles. The Job page's tenant footer has no
              Cancel because that modal is dismissed elsewhere; this is a
              standalone dialog opened from a queue row, and removing its only
              in-footer dismissal would be a regression, not parity. */}
          <button
            type="button"
            className="dashv2-ehcancel"
            onClick={onClose}
            disabled={submitting}
          >
            Cancel
          </button>
          {!clientAdmin && (
            <button
              type="button"
              className="dashv2-ehsave"
              onClick={() => submit(true)}
              disabled={submitting}
            >
              Save Changes
            </button>
          )}
          <button
            type="button"
            className="dashv2-ehresolve"
            onClick={() => submit(false)}
            disabled={submitting}
          >
            {submitting ? 'Saving…' : clientAdmin ? 'Resolve' : 'Approve'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EnterHoursDialog;
